import { useOnValueChange } from '@calendly/hooks'
import { I18n } from '@calendly/i18n'
import { Button } from '@calendly/ui/components/button'
import { Popup, Header, Title, Body, Footer } from '@calendly/ui/components/popup'
import { TextField } from '@calendly/ui/form/text-field'
import { zIndexModal } from '@calendly/ui/z-index'
import { useState } from 'react'

import { useGetUserQuery } from 'common'
import { useBlockInviteeMutation } from 'shared/api/meetings'

import {
  Subtext,
  HeaderInner,
  Label,
  StepIndicator,
  ProgressBar,
  ProgressBarWrapper,
  WarningSubtext,
} from './styles'

/**
 * Step 2 of the block-invitee flow: confirm reason and submit.
 *
 * @param {Object} props
 * @param {Object} props.event - The event for this invitee. Required; used for meetingName and eventId in the block API.
 * @param {string|number} [props.event.id] - Event id sent to the block API.
 * @param {string} [props.event.name] - Meeting name sent to the block API.
 * @param {Object} props.invitee - The invitee being blocked. Must have `uuid`, `name`, `email`.
 * @param {string} props.reasonType - The selected reason type key (e.g. 'spam').
 * @param {() => void} props.onBack - Return to reason step.
 * @param {() => void} props.onClose - Close the modal.
 * @param {() => void} props.onSuccess - Called after successful submit (move to success step).
 */
export const BlockSubmitModal = ({
  event,
  invitee,
  reasonType,
  onBack,
  onClose,
  onSuccess
}) => {
  const { data: currentUser } = useGetUserQuery()
  const [additionalInfo, setAdditionalInfo] = useState('')
  const [blockInvitee, { isLoading, isSuccess, isError }] = useBlockInviteeMutation()

  useOnValueChange(isSuccess, () => {
    if (isSuccess) {
      onSuccess()
    }
  })

  useOnValueChange(isError, () => {
    if (isError) {
      onClose()
    }
  })

  const onReasonInputChange = ({ target: { value } }) => setAdditionalInfo(value)

  const onConfirm = () => {
    blockInvitee({
      inviteeUuid: invitee.uuid,
      hostId: currentUser.id,
      inviteeName: invitee.name,
      inviteeEmail: invitee.email,
      meetingName: event.name,
      reasonType,
      reason: additionalInfo,
      eventId: event.id
    })
  }

  return (
    <Popup onClose={onClose} zIndex={zIndexModal}>
      <Header>
        <HeaderInner>
          <ProgressBarWrapper>
            <StepIndicator>Step 2 of 2</StepIndicator>
            <ProgressBar step={2} total={2} />
          </ProgressBarWrapper>
          <Title>
            {I18n.t('publisher.block_invitee.submit_modal.title', { name: invitee.name })}
          </Title>
        </HeaderInner>
      </Header>
      <Body>
        <Subtext>{I18n.t('publisher.block_invitee.reason_modal.subtext')}</Subtext>
        <Label>
          {I18n.t('publisher.block_invitee.submit_modal.additional_info_section.label')}
          <span>
            <Label gray>{I18n.t('publisher.block_invitee.submit_modal.optional')}</Label>
          </span>
        </Label>
        <TextField
          type="textarea"
          name="report_info"
          value={additionalInfo}
          onChange={onReasonInputChange}
          aria-label={I18n.t('publisher.block_invitee.submit_modal.additional_info_section.label')}
          placeholder={I18n.t('publisher.block_invitee.submit_modal.placeholder')}
        />
        <WarningSubtext>
          {I18n.t('publisher.block_invitee.submit_modal.warning_subtext')}
        </WarningSubtext>
      </Body>
      <Footer>
        <Button
          disabled={isLoading}
          decoration="secondary-outline"
          onClick={onBack}
        >
          {I18n.t('publisher.action.back')}
        </Button>
        <Button
          loading={isLoading}
          disabled={isLoading}
          decoration="primary"
          onClick={onConfirm}
        >
          {I18n.t('publisher.action.submit')}
        </Button>
      </Footer>
    </Popup>
  )
}
